import './rater.excess.view.scss';

import {
  Alert,
  Box,
  FormLabel,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  ConvergeSectionTitle,
  ConvergeTooltip,
  NewConvergeButton,
} from '../../../../presentation/components';
import {
  ExcessRatingSignificantTermsAndConditions,
  ScoreModifier,
} from '../enterprise-rating/rater.significant-terms-and-conditions';
import {
  RaterEndorsementList,
  RaterSubjectivitiesList,
  getParsedValue,
} from '../coverage-rating';
import { RaterScheduleRating, hasErrors } from '../enterprise-rating';
import config, { convergeLogger } from '@converge/config';
import { useCallback, useEffect, useMemo } from 'react';
import {
  useRaterActionDispatchContext,
  useRaterStateContextSelector,
} from '../data';

import { Add } from '@mui/icons-material';
import { ErrorPageLayout } from '../../../../presentation';
import { NumberField } from '../common';
import { RaterCoverageListTableHeaderCell } from './header-cell';
import { RaterExcessTowerLayer } from './excess-tower-layer-detail';
import { formatDollarAmount } from '@converge/utils';
import { isConverge, numberFieldMaxValue } from '@converge/data';
import { useFormContext } from 'react-hook-form';

export const CELL_COUNT = 11;

export const RaterExcessView = () => {
  const {
    excessTowerLayers,
    isRequestingRatingUpdate,
    isEditable,
    upsertQuoteIsLoading,
  } = useRaterStateContextSelector(
    ({
      quote: { excessTowerLayers },
      isRequestingRatingUpdate,
      isEditable,
      upsertQuoteIsLoading,
    }) => {
      return {
        excessTowerLayers,
        isRequestingRatingUpdate,
        isEditable,
        upsertQuoteIsLoading,
      };
    }
  );

  const { actions, dispatch } = useRaterActionDispatchContext();

  const isDisabled =
    isRequestingRatingUpdate || !isEditable || upsertQuoteIsLoading;

  const hasConvergeParticipant = useMemo(() => {
    return excessTowerLayers.some((layer) =>
      layer.participants.some((p) => isConverge(p))
    );
  }, [excessTowerLayers]);

  if (!excessTowerLayers) {
    convergeLogger.error({
      excessTowerLayers,
    });
    return (
      <ErrorPageLayout
        minHeight={'50vh'}
        message="No excess tower layer list found"
        messageId="excessRater.error.noExcessTowerLayerList"
      />
    );
  }

  if (!Array.isArray(excessTowerLayers)) {
    convergeLogger.error({
      excessTowerLayers,
    });
    return (
      <ErrorPageLayout
        minHeight={'50vh'}
        message="Excess tower layer is not a list"
        messageId="excess.error.excessTowerLayerIsNotAList"
      />
    );
  }

  return (
    <>
      <RaterExcessFactorsView isDisabled={isDisabled} />

      <Box className="tower-view-container">
        <Box className="tower-title">
          <ConvergeSectionTitle mb={0}>Tower</ConvergeSectionTitle>
        </Box>

        {isEditable &&
          (config.featureFlags.enableAddConvergeToExcessTowerPrompt ||
            excessTowerLayers.length === 1) && (
            <Slide in={!hasConvergeParticipant} unmountOnExit>
              <Alert
                color="info"
                className="tower-alert"
                action={
                  <NewConvergeButton
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={() =>
                      dispatch(actions.addConvergeToFirstExcessLayer())
                    }
                    label={'Add Converge to excess layer 1'}
                  />
                }
              >
                Please add Converge as a participant in the insurance tower to
                run excess rating.
              </Alert>
            </Slide>
          )}

        <Box className="excess-tower-Layer-Table-header-view">
          <Table size="small">
            <ExcessTowerLayerTableHeader />
            <TableBody>
              {excessTowerLayers.map((layer, index) => (
                <RaterExcessTowerLayer
                  key={index}
                  layer={layer}
                  isDisabled={isDisabled}
                  isLastLayer={index === excessTowerLayers.length - 1}
                />
              ))}
            </TableBody>
          </Table>
        </Box>

        <Box className="endorsements-Subjectivities-title-view ">
          <ConvergeSectionTitle mb={0}>
            Endorsements & Subjectivities
          </ConvergeSectionTitle>
        </Box>

        <Box className="endorsements-Subjectivities-table-view">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell />
                <TableCell>
                  <Typography className="included-style">Included</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <RaterEndorsementList mode="excess" />
              <RaterSubjectivitiesList mode="excess" />
            </TableBody>
          </Table>
        </Box>
      </Box>
    </>
  );
};

const ExcessTowerLayerTableHeader = () => {
  return (
    <TableHead component="thead" className="excess-tower-main-Header-table">
      <TableRow>
        <RaterCoverageListTableHeaderCell>
          Insurer
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Policy Number
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          <ConvergeTooltip title="Should be between $1 and $25M">
            <RaterCoverageListTableHeaderCell.Typography>
              Limit
            </RaterCoverageListTableHeaderCell.Typography>
          </ConvergeTooltip>
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Quota Share
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          Attachment Point
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          <ConvergeTooltip title="Minimum premium is based on QBE's underwriting guidelines">
            <RaterCoverageListTableHeaderCell.Typography>
              Minimum Premium
            </RaterCoverageListTableHeaderCell.Typography>
          </ConvergeTooltip>
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          <ConvergeTooltip title="Benchmark premium is the maximum of the premium calculated from Converge's excess rate manual, the premium at 80% ILF, and the minimum premium">
            <RaterCoverageListTableHeaderCell.Typography>
              Benchmark Premium
            </RaterCoverageListTableHeaderCell.Typography>
          </ConvergeTooltip>
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          <RaterCoverageListTableHeaderCell.Typography>
            Premium
          </RaterCoverageListTableHeaderCell.Typography>
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          <ConvergeTooltip title="Premium in layer as percent of premium in immediately underlying layer">
            <RaterCoverageListTableHeaderCell.Typography>
              % of Underlying Premium
            </RaterCoverageListTableHeaderCell.Typography>
          </ConvergeTooltip>
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell>
          <ConvergeTooltip title="Increased limit factor | PPM in layer as percent of PPM in immediately underlying layer">
            <RaterCoverageListTableHeaderCell.Typography>
              ILF
            </RaterCoverageListTableHeaderCell.Typography>
          </ConvergeTooltip>
        </RaterCoverageListTableHeaderCell>
        <RaterCoverageListTableHeaderCell />
      </TableRow>
    </TableHead>
  );
};

const RaterExcessFactorsView = ({ isDisabled = false }) => {
  const {
    excessSelectedCorrectiveFactor,
    excessSelectedCorrectiveFactorNew,
    excessMinCorrectiveFactor,
    excessMaxCorrectiveFactor,
    scheduleRatings,
    excessCorrectiveFactor,
    ratedPremium,
    excessTowerLayers,
    retention,
    hasExcessSelectedCorrectiveFactorChanged,
  } = useRaterStateContextSelector(
    ({
      quote: {
        excessSelectedCorrectiveFactor,
        excessSelectedCorrectiveFactorNew,
        excessMinCorrectiveFactor,
        excessMaxCorrectiveFactor,
        scheduleRatings,
        excessCorrectiveFactor,
        ratedPremium,
        excessTowerLayers,
        retention,
        hasExcessSelectedCorrectiveFactorChanged,
      },
    }) => {
      return {
        excessSelectedCorrectiveFactor,
        excessSelectedCorrectiveFactorNew,
        excessMinCorrectiveFactor,
        excessMaxCorrectiveFactor,
        scheduleRatings,
        excessCorrectiveFactor,
        ratedPremium,
        excessTowerLayers,
        retention,
        hasExcessSelectedCorrectiveFactorChanged,
      };
    }
  );

  const { dispatch, actions } = useRaterActionDispatchContext();

  const { primaryLayer, primaryLayerBoundPremium, primaryLayerLimit } =
    useMemo(() => {
      const primaryLayer = excessTowerLayers.find(
        (layer) => layer.primary || layer.position === 0
      );

      return {
        primaryLayer,
        primaryLayerBoundPremium: primaryLayer?.boundPremium,
        primaryLayerLimit: primaryLayer?.limit,
      };
    }, [excessTowerLayers]);

  const {
    formState: { errors },
    setError,
    clearErrors,
  } = useFormContext();

  const hasInvalidSelectedPremium = hasErrors(
    'primaryLayerBoundPremium',
    errors
  );

  useEffect(() => {
    dispatch(actions.setIsInvalidRaterData(hasInvalidSelectedPremium));
  }, [actions, dispatch, hasInvalidSelectedPremium]);

  const handleUpdatePrimaryLayerPremium = useCallback(
    (newValue: number | null) => {
      const parsedValue = getParsedValue(newValue);
      if (!parsedValue) {
        setError('primaryLayerBoundPremium', {
          type: 'primaryLayerBoundPremium',
          message: `Premium must be a greater than 0`,
        });
        return;
      }
      if (!primaryLayer) {
        throw new Error('No primary layer found');
      }

      dispatch(
        actions.updateExcessTowerLayer(primaryLayer.uuid, {
          boundPremium: newValue ?? null,
        })
      );
      clearErrors(`primaryLayerBoundPremium`);
    },
    [actions, clearErrors, dispatch, primaryLayer, setError]
  );

  const handleUpdateLimit = useCallback(
    (newValue: number | null) => {
      if (!primaryLayer) {
        throw new Error('No primary layer found');
      }

      dispatch(
        actions.updateExcessTowerLayer(primaryLayer.uuid, {
          limit: newValue ?? 0,
          cascadeToParticipants: true,
        })
      );
    },
    [actions, dispatch, primaryLayer]
  );

  const handleUpdateRetention = useCallback(
    (newValue: number | null) => {
      if (!primaryLayer) {
        throw new Error('No primary layer found');
      }

      dispatch(
        actions.updateExcessTowerLayer(primaryLayer.uuid, {
          attachmentPoint: newValue ?? null,
        })
      );
    },
    [actions, dispatch, primaryLayer]
  );

  return (
    <Stack className="rater-excess-factors-view-container" gap={2}>
      <Stack
        flexDirection={'row'}
        alignItems={'start'}
        gap={2}
        flexGrow={1}
        minWidth="100%"
      >
        <Box className="excess-rating-area">
          <Stack className="excess-rating-container" gap={3}>
            <Stack className="excess-rating-title-area">
              <ConvergeSectionTitle>Excess Rating</ConvergeSectionTitle>
              <FormLabel>Converge’s Calculated Primary Premium</FormLabel>
              <Typography
                fontWeight={600}
                fontSize="14px"
                sx={{ color: 'black' }}
              >
                {formatDollarAmount(ratedPremium)}
              </Typography>
            </Stack>
            <Stack flexGrow={1} minWidth={200}>
              <ConvergeSectionTitle
                marginTop={0}
                sx={{
                  opacity: 0,
                }}
              >
                Excess rating
              </ConvergeSectionTitle>
              <FormLabel>Primary Insurer's Premium</FormLabel>
              <NumberField
                isCurrency
                name={`primaryLayerBoundPremium`}
                value={primaryLayerBoundPremium}
                max={numberFieldMaxValue}
                isDisabled={isDisabled || !primaryLayer}
                onBlur={handleUpdatePrimaryLayerPremium}
                hasChanged={primaryLayer?.hasChanged}
                error={hasInvalidSelectedPremium}
              />
            </Stack>
            <Stack flexGrow={1} minWidth={200}>
              <ConvergeSectionTitle
                marginTop={0}
                sx={{
                  opacity: 0,
                }}
              >
                Excess rating
              </ConvergeSectionTitle>

              <FormLabel>Primary Insurer's Limit</FormLabel>
              <NumberField
                isCurrency
                name={`primaryLayerLimit`}
                value={primaryLayerLimit}
                max={numberFieldMaxValue}
                isDisabled
                // isDisabled={isDisabled || !primaryLayer}
                onBlur={handleUpdateLimit}
              />
            </Stack>

            <Stack flexGrow={1} minWidth={200}>
              <ConvergeSectionTitle
                marginTop={0}
                sx={{
                  opacity: 0,
                }}
              >
                Excess rating
              </ConvergeSectionTitle>

              <FormLabel>Primary Insurer's Retention</FormLabel>
              <NumberField
                isCurrency
                name={`retention`}
                value={retention}
                max={numberFieldMaxValue}
                isDisabled
                // isDisabled={isDisabled || !primaryLayer}
                onBlur={handleUpdateRetention}
              />
            </Stack>

            <Stack flexGrow={1} width="20%">
              <ConvergeSectionTitle marginTop={0}>
                Corrective Factor
              </ConvergeSectionTitle>
              <FormLabel>Modifier</FormLabel>
              <ScoreModifier
                isDisabled={isDisabled}
                onChange={(newValue: number | null) => {
                  dispatch(
                    actions.setQuoteField(
                      'excessSelectedCorrectiveFactor',
                      newValue
                    )
                  );
                }}
                fallbackPlaceholder={
                  excessSelectedCorrectiveFactorNew ??
                  excessCorrectiveFactor ??
                  undefined
                }
                min={excessMinCorrectiveFactor ?? 0.75}
                max={excessMaxCorrectiveFactor ?? 1.25}
                selected={excessSelectedCorrectiveFactor}
                hasChanged={hasExcessSelectedCorrectiveFactorChanged}
              />
            </Stack>
          </Stack>
        </Box>
      </Stack>

      <Stack
        flexDirection={'row'}
        alignItems={'start'}
        gap={3}
        flexGrow={1}
        minWidth="100%"
      >
        <Box flexGrow={1} flexShrink={0}>
          <ConvergeSectionTitle mt={0}>Schedule Rating</ConvergeSectionTitle>

          <RaterScheduleRating
            showTitle={false}
            scheduleRatings={scheduleRatings.filter(
              (scheduleRating) => scheduleRating.excess
            )}
            hideModifier
            onChange={(uuid, percentage) => {
              dispatch(actions.setScheduleRatingPercentage(uuid, percentage));
            }}
            minPercentage={-10}
            maxPercentage={10}
            maxTotalPercentage={25}
            minTotalPercentage={-25}
          />
        </Box>
        <Box flexGrow={1} minWidth="400px" flexShrink={0}>
          <ConvergeSectionTitle mt={3}>
            Significant Terms and Conditions
          </ConvergeSectionTitle>
          <ExcessRatingSignificantTermsAndConditions />
        </Box>
      </Stack>
    </Stack>
  );
};
